import { useEffect } from 'react';
import { X } from 'lucide-react';

type Size = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: Size;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const sizeClasses: Record<Size, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

/**
 * 模态框。
 *
 * 设计系统（DESIGN.md §10）：
 * - 圆角从 24px 任意值收到 rounded-md (8px)
 * - Overlay 用暖色墨黑 ironwood/50，不再是冷色 slate
 * - 去掉过度大号阴影（shadow-2xl），改用暖色 shadow-pop
 * - 分割线用 birch-200 暖边，页脚底色改 pine-100
 * - Esc 关闭 / 点击 overlay 关闭 / 打开时 body 禁滚
 */
export default function Modal({ open, onClose, title, size = 'md', footer, children }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ironwood/50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className={`w-full ${sizeClasses[size]} overflow-hidden rounded-md bg-white shadow-pop`}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="flex items-center justify-between border-b border-birch-200 px-5 py-3.5">
            <h2 className="text-md font-semibold text-ironwood">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-sm p-1.5 text-oak-500 transition-colors hover:bg-pine-100 hover:text-ironwood"
              aria-label="关闭"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-birch-200 bg-pine-100 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
